// Buyer fee tiers (backend services/order/orderByFee.js): display helpers shared by
// the Order By page (where the fee is edited) and the Dispatch Bot page (read-only).

export const PARTS = [
  { field: 'percent', label: '%', help: 'of the base, in yen' },
  { field: 'fixedJpy', label: 'Fixed ¥', help: 'added in yen' },
  { field: 'fixedThb', label: 'Fixed ฿', help: 'added in baht' },
  { field: 'rate', label: 'Rate ฿/¥', help: "buyer's own rate (0.225): base × (rate − today's rate), in baht" },
];

export const tierText = (t) => [
  t.percent != null && `${t.percent}%`,
  t.fixedJpy != null && `¥${Number(t.fixedJpy).toLocaleString()}`,
  t.fixedThb != null && `฿${t.fixedThb}`,
  t.rate != null && `rate ${t.rate}`,
].filter(Boolean).join(' + ');

/**
 * Readable fee, one line per tier ("≤ ¥19,999: 5%", …, "above: ¥10,000"), or the old
 * fields (flagged `old`) until P2.4 moves everyone. `text` joins the lines for one-line use.
 */
export const feeSummary = (b) => {
  if (Array.isArray(b.feeTiers) && b.feeTiers.length > 0) {
    const lines = b.feeTiers.length === 1 && b.feeTiers[0].upToJpy == null
      ? [tierText(b.feeTiers[0])]
      : b.feeTiers.map((t) => `${t.upToJpy != null ? `≤ ¥${Number(t.upToJpy).toLocaleString()}` : 'above'}: ${tierText(t)}`);
    return { lines, text: lines.join(' · '), old: false };
  }
  const old = [
    b.feePercentage != null && `${b.feePercentage}%`,
    b.feeRate != null && `rate ${b.feeRate}`,
    b.feeFixedJpy != null && `¥${Number(b.feeFixedJpy).toLocaleString()}`,
  ].filter(Boolean).join(' + ');
  const text = old ? `old: ${old}` : 'none';
  return { lines: [text], text, old: true };
};

/** Editor rows ↔ saved tiers. A row holds strings; '' = part not set. */
export const EMPTY_ROW = { upToJpy: '', percent: '', fixedJpy: '', fixedThb: '', rate: '' };

export const toRows = (tiers) => tiers.map((t) => ({
  upToJpy: t.upToJpy ?? '', percent: t.percent ?? '', fixedJpy: t.fixedJpy ?? '', fixedThb: t.fixedThb ?? '', rate: t.rate ?? '',
}));

/** A buyer without tiers starts from their old fee fields, so the admin confirms rather than retypes. */
export const rowsFromLegacy = (b) => {
  if (b.feePercentage != null) return [{ ...EMPTY_ROW, percent: b.feePercentage, fixedJpy: b.feeFixedJpy ?? '' }];
  if (b.feeRate != null) return [{ ...EMPTY_ROW, rate: b.feeRate, fixedJpy: b.feeFixedJpy ?? '' }];
  if (b.feeFixedJpy != null) return [{ ...EMPTY_ROW, fixedJpy: b.feeFixedJpy }];
  return [];
};

export const toTiers = (rows) => rows.map((r) => {
  const tier = {};
  if (String(r.upToJpy).trim() !== '') tier.upToJpy = Number(r.upToJpy);
  for (const p of PARTS) if (String(r[p.field]).trim() !== '') tier[p.field] = Number(r[p.field]);
  return tier;
});

/** Client-side check before saving; the backend validates the rest. @returns {string|null} */
export const rowsError = (rows) => {
  const tiers = toTiers(rows);
  const bad = tiers.findIndex((t, i) => (i < tiers.length - 1 && t.upToJpy == null) || PARTS.every((p) => t[p.field] == null));
  return bad >= 0 ? `Fee row ${bad + 1}: needs "Up to ¥" (except the last row) and at least one of %, fixed ¥, fixed ฿, rate.` : null;
};
